/**
 * Finance › **Carteira de devedores** (W8-FINAL-FARE-AND-CASH, D438; finding A-02) — the pure view-model behind the
 * debtor portfolio screen over `GET /v1/finance/driver-debts?market=` (`payout:view`).
 *
 * Every driver of a market whose commission receivable exceeds his payable owes the platform; the server ages that debt
 * FIFO into buckets (0–7, 8–30, 31–60, 61+ days) and flags a debt that predates the dated ledger history
 * (`undatedRemainder`, it sits in the oldest bucket). The console only renders it:
 *  - the market comes from the operator's scope (never typed), and nothing is read until one is chosen;
 *  - a refusal is an ERROR the screen explains (`MARKET_REQUIRED`, `MARKET_INVALID`, `PAYOUT_CURRENCY_REQUIRED`,
 *    `DEBTS_SOURCE_UNAVAILABLE`, a bare 403) — never an empty portfolio that reads as "nobody owes";
 *  - amounts are exact integer minor units (bigint) in the portfolio's one currency; a malformed amount is shown as
 *    unknown, never as zero.
 *
 * Neutral: markets, currencies and bucket ids are data.
 */
import { type ApiError, type CashHandoverApi, type DriverDebtBucket, type DriverDebtPortfolio } from '@vello/api-client';
import { type Result } from '@vello/core';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** The buckets the console knows, youngest first (the server sends its own order; unknown ids are dropped). */
export const DRIVER_DEBT_BUCKETS: readonly DriverDebtBucket[] = ['D0_7', 'D8_30', 'D31_60', 'D61_PLUS'];

const MINOR = /^\d+$/;
const minor = (raw: string | undefined): bigint | undefined => (raw !== undefined && MINOR.test(raw) ? BigInt(raw) : undefined);

export interface DriverDebtRowView {
  readonly driverId: string;
  /** Undefined when the server sent an amount the console cannot read (shown as unknown, never zero). */
  readonly debtMinor: bigint | undefined;
  readonly aging: Readonly<Record<DriverDebtBucket, bigint | undefined>>;
  readonly oldestAt: string | undefined;
  readonly undatedRemainder: boolean;
}

export interface DriverDebtPortfolioView {
  readonly marketId: string;
  readonly currency: string;
  readonly asOf: string;
  readonly policyVersion: string;
  readonly buckets: readonly DriverDebtBucket[];
  readonly totalMinor: bigint | undefined;
  readonly totals: Readonly<Record<DriverDebtBucket, bigint | undefined>>;
  readonly rows: readonly DriverDebtRowView[];
  /** How many debtors carry a debt older than the dated history. */
  readonly undatedCount: number;
}

/** The portfolio as the screen renders it: known buckets in the server's order, exact bigint amounts. */
export function driverDebtPortfolioView(p: DriverDebtPortfolio): DriverDebtPortfolioView {
  const buckets = p.buckets.filter((b) => (DRIVER_DEBT_BUCKETS as readonly string[]).includes(b));
  const ordered = buckets.length > 0 ? buckets : DRIVER_DEBT_BUCKETS;
  const byBucket = (rec: Readonly<Record<string, string>>): Record<DriverDebtBucket, bigint | undefined> =>
    Object.fromEntries(DRIVER_DEBT_BUCKETS.map((b) => [b, minor(rec[b])])) as Record<DriverDebtBucket, bigint | undefined>;
  const rows = p.debtors.map((d) => ({
    driverId: d.driverId,
    debtMinor: minor(d.debtMinor),
    aging: byBucket(d.aging),
    oldestAt: d.oldestAt ?? undefined,
    undatedRemainder: d.undatedRemainder === true,
  }));
  return {
    marketId: p.marketId,
    currency: p.currency,
    asOf: p.asOf,
    policyVersion: p.policyVersion,
    buckets: ordered,
    totalMinor: minor(p.totals.debtMinor),
    totals: byBucket(p.totals),
    rows,
    undatedCount: rows.filter((r) => r.undatedRemainder).length,
  };
}

export type DriverDebtScreenState = 'no_permission' | 'needs_market' | 'loading' | 'refused' | 'empty' | 'ready';

/** What the portfolio panel shows. A refusal is never "empty"; no market is never a read. */
export function driverDebtScreenState(input: { readonly mayView: boolean; readonly market: string; readonly error?: string; readonly portfolio?: DriverDebtPortfolioView }): DriverDebtScreenState {
  if (!input.mayView) return 'no_permission';
  if (input.market.trim() === '') return 'needs_market';
  if (input.error !== undefined) return 'refused';
  if (input.portfolio === undefined) return 'loading';
  return input.portfolio.rows.length === 0 ? 'empty' : 'ready';
}

/** Who may open the portfolio (`payout:view` is PLATFORM on the server, like every payout route). Unknown → the server. */
export function driverDebtAuthority(grants: KnownGrants): { readonly mayView: boolean } {
  // D438 review (W8F-07): the server authorizes against the market asked for, so a CITY/COUNTRY finance grant reads its own.
  return { mayView: grants === undefined || grantsAllow(grants, { permission: 'payout:view', target: 'GEO' }) };
}

/** The market's debtor portfolio (the server fails closed — see the module comment). */
export function loadDriverDebts(runtime: { readonly cashHandovers: CashHandoverApi }, market: string): Promise<Result<DriverDebtPortfolio, ApiError>> {
  return runtime.cashHandovers.driverDebts(market.trim());
}
